import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUserAPI } from '../../contexts/UserAPIContext';
import { useNotification } from '../../contexts/NotificationContext';
import { getImageUrl } from '../../firebase/storageService';
import { authFetch } from '../../utils/authFetch';

const Reposicoes = () => {
  const { userAPIData } = useUserAPI();
  const [reposicoes, setReposicoes] = useState([]);
  const [filteredReposicoes, setFilteredReposicoes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [periodo, setPeriodo] = useState({ data_inicio: '', data_fim: '' });
  const [loading, setLoading] = useState(true);
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const { showNotification } = useNotification();

  // Função para carregar imagens de forma assíncrona
  const loadImages = async (data) => {
    for (const item of data) {
      try {
        setImageLoadingStates(prev => ({ ...prev, [item.patientId]: true }));
        const url = await getImageUrl('patient', item.patientId);
        setReposicoes(prevReposicoes =>
          prevReposicoes.map(p =>
            p.patientId === item.patientId
              ? { ...p, imageUrl: url }
              : p
          )
        );
      } catch (error) {
        console.error(`Erro ao carregar imagem para paciente ${item.patientId}:`, error);
        setReposicoes(prevReposicoes =>
          prevReposicoes.map(p =>
            p.patientId === item.patientId
              ? { ...p, imageUrl: require('../../assets/img/perfilvazio.png') }
              : p
          )
        );
      } finally {
        setImageLoadingStates(prev => ({ ...prev, [item.patientId]: false }));
      }
    }
  };

  useEffect(() => {
    const fetchReposicoes = async () => {
      try {
        const response = await authFetch('appointment/rescheduling', {});
        const data = await response.json();
        
        // Primeiro setamos os dados sem as imagens
        const reposicoesComImagemNull = data.map(item => ({ ...item, imageUrl: null }));
        setReposicoes(reposicoesComImagemNull);
        setFilteredReposicoes(reposicoesComImagemNull);
        setLoading(false);

        // Depois carregamos as imagens de forma assíncrona
        loadImages(data);

      } catch (error) {
        showNotification('danger', 'Erro ao buscar dados de reposição');
        setLoading(false);
      }
    };

    fetchReposicoes();
  }, []);

  useEffect(() => {
    const fetchPeriodo = async () => {
      try {
        const response = await authFetch('config-parameter', {});
        const data = await response.json();        
        setPeriodo({
          data_inicio: data.appointmentRescheduleStartDate,
          data_fim: data.appointmentRescheduleEndDate
        });
      } catch (error) {
        showNotification('Erro ao buscar dados de reposição', 'error');
      }
    };

    fetchPeriodo();
  }, []);

  // Efeito para filtrar os dados quando o termo de busca muda
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredReposicoes(reposicoes);
      return;
    }

    const searchTermLower = searchTerm.toLowerCase().trim();
    const filtered = reposicoes.filter(reposicao => 
      reposicao.patientName.toLowerCase().includes(searchTermLower)
    );
    setFilteredReposicoes(filtered);
  }, [searchTerm, reposicoes]);

  // Handler para mudança no campo de busca
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="content">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="font-weight-bolder mb-0">Reposições</h3>
        {userAPIData.userRoleId === 1 && (
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Link to="/Calendario/Gerenciamento" className="btn btn-info btn-sm">
              Zeramento de Reposições
            </Link>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-body p-3">

          <div className="col-md-12 row">
            <div className="col-md-6">
              <h6>Período Considerado</h6>
              <div className="d-flex gap-2">
                <div className="col-md-4">
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    readOnly
                    value={periodo.data_inicio}
                  />
                </div>
                <div className="col-md-4">
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    readOnly
                    value={periodo.data_fim}
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <h6>&nbsp;</h6>
              <div className="input-group input-group-sm">
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nome..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
          </div>

          <div className="table-responsive mt-3">
            {loading ? (
              <p>Carregando...</p>
            ) : (
              <table className="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Criança
                    </th>
                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                      Faltas com Reposição
                    </th>
                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                      Reposições Pagas
                    </th>
                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                      Reposições Faltantes
                    </th>
                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReposicoes.length > 0 ? (
                    filteredReposicoes.map((a) => (
                      <tr key={a.patientId}>
                        <td className="text-xs mb-0">
                          <div className="d-flex px-2 py-1">
                            <div style={{ position: 'relative', width: '36px', height: '36px' }}>
                              {imageLoadingStates[a.patientId] && (
                                <div className="position-absolute top-50 start-50 translate-middle">
                                  <div className="spinner-border text-primary" role="status" style={{ width: '20px', height: '20px' }}>
                                    <span className="visually-hidden">Carregando...</span>
                                  </div>
                                </div>
                              )}
                              <img
                                src={a.imageUrl || require('../../assets/img/perfilvazio.png')}
                                alt="Foto"
                                className="avatar avatar-sm me-3"
                                style={{ 
                                  borderRadius: '1.75em',
                                  width: '36px',
                                  height: '36px',
                                  objectFit: 'cover',
                                  opacity: imageLoadingStates[a.patientId] ? 0.3 : 1
                                }}
                              />
                            </div>
                            <div className="d-flex flex-column justify-content-center ms-3">
                              <p className="mb-0 text-sm">{a.patientName}</p>
                            </div>
                          </div>
                        </td>
                        <td className="text-xs mb-0 text-center">
                          <p className="mb-0 text-sm">{a.rescheduling}</p>
                        </td>
                        <td className="text-xs mb-0 text-center">
                          <p className="mb-0 text-sm">{a.reschedulingDone}</p>
                        </td>
                        <td className="text-xs mb-0 text-center">
                          <p className="mb-0 text-sm">
                            {a.rescheduling - a.reschedulingDone}
                          </p>
                        </td>
                        <td className="text-xs mb-0 text-center">
                          <Link
                            to={`/Calendario/Reposicoes/Detalhes/Paciente/${a.patientId}`}
                            title="Visualizar Detalhes"
                          >
                            <i className="fa-regular fa-eye text-primary text-gradient"></i>
                          </Link>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        {searchTerm ? 'Nenhuma reposição encontrada para a busca.' : 'Nenhuma reposição encontrada.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reposicoes;
